/**
 * When a producer is asked to confirm a booked drop-off.
 *
 * One definition for the three places that ask the question — the API's
 * dashboard count and list, the job page's prompt, and the dashboard's
 * confirm list — so none of them can start asking on a different day.
 *
 * `dropoffDate` is stored as midnight UTC of the drop-off day, and the day
 * itself still counts: a drop-off is "ahead" until a day after that instant.
 */

/** How many days before the drop-off the producer is asked. */
export const DROPOFF_CONFIRM_WINDOW_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The `dropoffDate` range whose jobs are inside the window right now, for a
 * database query: `$gt: after, $lte: until`.
 * @param now - The moment to judge against, in ms.
 * @returns The exclusive lower and inclusive upper bound.
 */
export function dropoffConfirmWindowBounds(now: number = Date.now()): {
  after: Date;
  until: Date;
} {
  return {
    after: new Date(now - DAY_MS),
    until: new Date(now + DROPOFF_CONFIRM_WINDOW_DAYS * DAY_MS),
  };
}

/**
 * Whether the drop-off day has not yet passed.
 * @param dropoffDate - The job's drop-off date.
 * @param now - The moment to judge against, in ms.
 * @returns True until the end of the drop-off day (UTC).
 */
export function isDropoffAhead(
  dropoffDate: Date | string | number,
  now: number = Date.now(),
): boolean {
  return (
    new Date(dropoffDate).getTime() >
    dropoffConfirmWindowBounds(now).after.getTime()
  );
}

/**
 * Whether the drop-off is close enough to ask about: still ahead, and inside
 * the last {@link DROPOFF_CONFIRM_WINDOW_DAYS} days.
 * @param dropoffDate - The job's drop-off date.
 * @param now - The moment to judge against, in ms.
 * @returns True when the producer should be asked now.
 */
export function isInDropoffConfirmWindow(
  dropoffDate: Date | string | number,
  now: number = Date.now(),
): boolean {
  const t = new Date(dropoffDate).getTime();
  const { after, until } = dropoffConfirmWindowBounds(now);
  return t > after.getTime() && t <= until.getTime();
}
